import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { ratingToFormattedRating } from '@alltrails/data-formatting/numberFormatters';
import Button from '@alltrails/denali/components/Button';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import type { TrailPhotoLocationInfo } from '@alltrails/trails';
import type { TrailId } from '@alltrails/types/TrailId';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import useTrailDefaultImage from '@/hooks/useTrailDefaultImage';
import styles from './Reviews.module.scss';

type Props = {
  mapId: number;
  defaultPhoto?: TrailPhoto;
  trailInfo: TrailPhotoLocationInfo & { id: TrailId };
  handleAddReview: () => void;
};

const ReviewsNullState = ({ mapId, defaultPhoto, trailInfo, handleAddReview }: Props) => {
  const intl = useIntl();
  const { defaultPhotoAlt, defaultPhotoUrl } = useTrailDefaultImage({
    defaultPhoto,
    mapId,
    photoPreviewId: undefined,
    photoSize: '160x160',
    trailPhotoLocationInfo: trailInfo
  });
  const localizedRating = ratingToFormattedRating(4.8, intl);

  return (
    <div className={styles.nullStateContainer}>
      <div className={styles.nullImageContainer}>
        <Image className={styles.nullImage} alt={defaultPhotoAlt} src={defaultPhotoUrl} width={111} height={111} />
        <div className={styles.mockReviewContainer}>
          <div>{localizedRating}</div>
          <div className={styles.starRating}>
            {Array(5)
              .fill(1)
              .map((_, index) => {
                if (4 > index) {
                  return <StarFilled color="--color-text-primary" size="xs" key={`FILLED_STAR_${index}`} className={styles.star} />;
                }
                return <StarFilled color="--color-text-disabled" size="xs" key={`DISABLED_STAR_${index}`} className={styles.star} />;
              })}
          </div>
          <div className={styles.mockTextSkeleton1} />
          <div className={styles.mockTextSkeleton2} />
        </div>
      </div>
      <div className={styles.nullText}>
        <Typography variant="heading400" component="div">
          <FormattedMessage defaultMessage="Be the first to review" />
        </Typography>
        <Typography variant="paragraph200" component="div" color="secondary">
          <FormattedMessage defaultMessage="Help trailgoers know what to expect" />
        </Typography>
      </div>
      <Button
        className={styles.nullButton}
        onClick={handleAddReview}
        text={<FormattedMessage defaultMessage="Review trail" />}
        testId="review-trail"
        variant="primary"
      />
    </div>
  );
};

export default ReviewsNullState;
